import React, { useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { INVOICE_STATUSES } from "constants/statuses";
import { ROUTES } from "routes";

import { datetime, toDollar } from "utils";

import { useReferrerState } from "hooks";
import { useInternalInvoice, useInternalInvoiceDownload } from "hooks/api";
import { useSnackbar } from "contexts";
import { ArrowUpRightIcon, DownloadIcon, ReceiptTextIcon } from "lucide-react";

import { Box, Divider, Grid, Stack, Typography } from "@mui/material";

import { Button, Card, Skeleton } from "@repo/ui";
import {
  EmptyState,
  Header,
  CategoryCell,
  UserCell,
  PaymentCell,
  CompanyCell,
  // Shared with OrderDetailPage so the two internal detail surfaces stay
  // identical in label typography, gaps, and value alignment (§36).
  DetailField as Field,
  DetailSummaryRow as SummaryRow,
} from "components";
import { AddressPreview } from "components/display/AddressPreview";
import type { InvoiceLineItem } from "services/billingApi";

// Human label for a single line item — prefers the Stripe description (already
// "N × Plan (at $X / month)"), else builds "N × Product".
const lineItemLabel = (item: InvoiceLineItem): string =>
  item.description ||
  [
    item.quantity ? `${item.quantity} ×` : null,
    item.productName || item.planNickname || item.priceId || "Item",
  ]
    .filter(Boolean)
    .join(" ");

// Mirrors OrderDetailSkeleton — same Card/Grid footprint as the loaded page so
// there's zero layout shift (working-conventions §19; this page previously
// showed a bare centered <Loader /> spinner).
const InvoiceDetailSkeleton = () => (
  <Grid container spacing={4}>
    <Grid item xs={12} md={8}>
      <Card>
        <Stack spacing={4}>
          <Skeleton height={20} width="30%" />
          <Skeleton lines={4} />
        </Stack>
      </Card>
    </Grid>
    <Grid item xs={12} md={4}>
      <Card>
        <Stack spacing={3}>
          <Skeleton height={20} width="50%" />
          <Skeleton lines={5} />
        </Stack>
      </Card>
    </Grid>
  </Grid>
);

/**
 * Cross-company internal invoice detail (VERA-882). Shares the internal Order
 * detail page's layout vocabulary (VERA-891): `Card` + stacked `DetailField`s,
 * line items on the left at md=8, a two-column summary on the right at md=4
 * closing with a divider + Total.
 */
export const InternalInvoicePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { open: notify } = useSnackbar();
  const { data: invoice, isLoading } = useInternalInvoice(id);
  const downloadInvoice = useInternalInvoiceDownload();
  // Detail → detail: the derived label would be the Invoices list, not this invoice.
  const referrer = useReferrerState("Invoice");

  const handleDownload = useCallback(async () => {
    if (!invoice) return;
    try {
      const { url } = await downloadInvoice.mutateAsync(invoice.id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (error) {
      notify({
        type: "error",
        text: "Unable to download this invoice. Please try again.",
      });
    }
  }, [downloadInvoice, invoice, notify]);

  if (isLoading) {
    return <InvoiceDetailSkeleton />;
  }

  if (!invoice) {
    return (
      <EmptyState
        title="Invoice not found"
        text="We couldn't find the invoice you were looking for."
        icon={<ReceiptTextIcon strokeWidth={1} size={32} />}
        buttons={
          <Button
            variant="primary"
            disableElevation
            onClick={() => navigate(ROUTES.INTERNAL_INVOICES)}
          >
            Back to Invoices
          </Button>
        }
      />
    );
  }

  const displayId = invoice.number ?? invoice.id;

  const formattedDateTime = invoice.createdAt
    ? datetime(invoice.createdAt, "dd MMM, yyyy, h:mm a")
    : "—";

  // Type is resolved server-side (shared invoice.mapper resolveInvoiceType) so the
  // detail page and the invoices table always agree — no client re-resolution.
  const invoiceTypeChip = (
    <CategoryCell
      category={invoice.type ?? "other"}
      filter={false}
      align="start"
      bordered={false}
    />
  );

  const companyValue = invoice.companyId ? (
    <CompanyCell
      company={{
        id: invoice.companyId,
        name: invoice.companyName ?? "—",
        logo: null,
      }}
    />
  ) : (
    invoice.companyName ?? "—"
  );

  const hasCustomer = Boolean(invoice.customerName || invoice.customerEmail);
  const orderedByValue = hasCustomer ? (
    <UserCell
      user={{
        name: invoice.customerName || undefined,
        email: invoice.customerEmail || undefined,
      }}
    />
  ) : (
    "—"
  );

  const hasPaymentMethod = Boolean(
    invoice.paymentMethod &&
      (invoice.paymentMethod.brand ||
        invoice.paymentMethod.last4 ||
        invoice.paymentMethod.label)
  );
  const paymentMethodValue = hasPaymentMethod ? (
    <PaymentCell paymentMethod={invoice.paymentMethod} link={false} />
  ) : (
    "—"
  );

  const shippingValue = invoice.shipping ? (
    <AddressPreview address={invoice.shipping} />
  ) : (
    "—"
  );

  const discount = invoice.lineItems.reduce(
    (sum, item) => sum + (item.amountDiscount || 0),
    0
  );

  return (
    <>
      <Header
        title={`#${displayId}`}
        breadcrumbs
        status={{
          statuses: INVOICE_STATUSES,
          value: invoice.status ?? undefined,
        }}
        breadcrumbsOveride={displayId}
        rightComponents={
          <Stack direction="row" spacing={3} alignItems="center">
            {invoice.orderId && (
              <Button
                variant="secondary"
                disableElevation
                startIcon={<ArrowUpRightIcon strokeWidth={1} />}
                onClick={() => navigate(ROUTES.TAGS_ORDER(invoice.orderId!), { state: referrer })}
              >
                View order
              </Button>
            )}
            <Button
              variant="secondary"
              disableElevation
              startIcon={<DownloadIcon strokeWidth={1} />}
              disabled={downloadInvoice.isPending}
              onClick={handleDownload}
            >
              Download
            </Button>
          </Stack>
        }
      />
      {/* Details (the invoice's fields) fill the wide left card; the narrow
          right card is the Summary — line items, then subtotal/discount and
          the Total. Same card vocabulary as the internal Order detail page. */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Card>
            <Stack spacing={6}>
              <Typography variant="body1" fontWeight="medium">
                Details
              </Typography>

              {/* Each field is its OWN grid cell flowing row-major, so the two
                  columns stay aligned row-by-row the way the old InfoRow pairs
                  did. (Two independent column Stacks drifted out of step: a
                  short text value on the left and a taller avatar row on the
                  right advance at different rates.) A CSS grid rather than a MUI
                  Grid container — the latter's negative margin/padding pair
                  indented the first column past the card title. */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  alignItems: "start",
                  columnGap: 6,
                  rowGap: 6,
                }}
              >
                <Field label="Invoice ID">{`#${displayId}`}</Field>
                <Field label="Company">{companyValue}</Field>

                <Field label="Date">{formattedDateTime}</Field>
                <Field label="Ordered by">{orderedByValue}</Field>

                <Field label="Type">{invoiceTypeChip}</Field>
                {invoice.shipping && (
                  <Field label="Shipping address">{shippingValue}</Field>
                )}

                <Field label="Payment method">{paymentMethodValue}</Field>
              </Box>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <Stack spacing={6}>
              <Typography variant="body1" fontWeight="medium">
                Summary
              </Typography>

              {invoice.lineItems.length === 0 ? (
                <Typography variant="body3" color="text.secondary">
                  This invoice has no line items.
                </Typography>
              ) : (
                <Stack divider={<Divider />} spacing={3}>
                  {invoice.lineItems.map((item) => {
                    const title =
                      item.productName || item.planNickname || lineItemLabel(item);
                    // Only a subline when it ADDS something — with no
                    // productName/planNickname the title already falls back to
                    // the description, and repeating it read as a duplicate.
                    const subtitle =
                      item.description && item.description !== title
                        ? item.description
                        : null;

                    return (
                      <Stack
                        key={item.id}
                        spacing={1}
                        sx={{ pt: 3, "&:first-of-type": { pt: 0 } }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          spacing={3}
                        >
                          <Typography variant="body2">{title}</Typography>
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: "nowrap" }}
                          >
                            {typeof item.amount === "number"
                              ? toDollar(item.amount)
                              : "—"}
                          </Typography>
                        </Stack>
                        {subtitle && (
                          <Typography variant="body3" color="text.secondary">
                            {subtitle}
                          </Typography>
                        )}
                        {typeof item.quantity === "number" && (
                          <Typography variant="body3" color="text.secondary">
                            Qty {item.quantity}
                          </Typography>
                        )}
                      </Stack>
                    );
                  })}
                </Stack>
              )}

              <Stack spacing={3}>
                {typeof invoice.subtotal === "number" && (
                  <SummaryRow label="Subtotal">
                    {toDollar(invoice.subtotal)}
                  </SummaryRow>
                )}
                {discount > 0 && (
                  <SummaryRow label="Discount">{`- ${toDollar(discount)}`}</SummaryRow>
                )}
              </Stack>

              <Divider />

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Total
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {typeof invoice.total === "number"
                    ? toDollar(invoice.total)
                    : "—"}
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};
